import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Grid, Button,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  list_school_instructors_api, update_instructor_compensation_api,
  update_school_tutors_config_api,
} from "../../../DAL/SchoolPortal/Tutors";
import { currencySymbol } from "../../../utils/region";

/**
 * What instructors are PAID — as distinct from what students are CHARGED,
 * which is the pricing block above this one.
 *
 * The distinction is the whole reason this exists. The two numbers move for
 * unrelated reasons: a school raises its prices because of the market and
 * changes someone's pay because of an employment conversation, and a settings
 * screen that offers one control for both forces every price change through a
 * pay negotiation.
 *
 * THREE ARRANGEMENTS, BECAUSE SCHOOLS USE ALL THREE
 *   Share      a percentage of what the session earned
 *   Flat rate  a set amount per session whatever the student paid — the
 *              retained-CFI arrangement, and impossible to say as a percentage
 *   Nothing    the school keeps it all, because the instructor is salaried and
 *              is already paid for this hour
 *
 * SET PER SESSION TYPE. A class is not a long 1:1 — one instructor hour, eight
 * students, different economics — so the two are configured separately rather
 * than one rule being stretched over both.
 *
 * PER PERSON, ON TOP. The school default covers most people; the list below
 * is for the ones on their own arrangement. Every row says whether it is
 * showing that person's own deal or the school's, because "70%" with no
 * provenance is the number that starts an argument on payout day.
 */

const MODES = [
  { value: "percent", label: "A share of the session" },
  { value: "fixed", label: "A flat rate per session" },
  { value: "school", label: "Nothing — the school keeps it" },
];

const DURATIONS = [["min10", "10 min"], ["min30", "30 min"], ["min60", "60 min"]];

const label = {
  fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 600, color: "#1a1a1a",
};
const hint = {
  fontFamily: "'Rubik', sans-serif", fontSize: "12.5px", color: "#8a8580", lineHeight: 1.5,
};

const inputSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "9px", fontFamily: "'Rubik', sans-serif", fontSize: "13px" },
  "& .MuiInputLabel-root": { fontFamily: "'Rubik', sans-serif", fontSize: "13px" },
};

/** Plain English for a resolved rule — the same sentence everywhere it appears. */
export function describeRule(rule, symbol) {
  if (!rule || !rule.mode) return "Following the school default";
  if (rule.mode === "school") return "Nothing — the school keeps it";
  if (rule.mode === "percent") return `${rule.percent ?? 0}% of the session`;
  if (rule.fixedPerClass != null) return `${symbol}${rule.fixedPerClass} per class`;
  const set = DURATIONS
    .filter(([k]) => rule.fixed?.[k] != null)
    .map(([k, t]) => `${symbol}${rule.fixed[k]} / ${t}`);
  return set.length ? set.join(" · ") : "A flat rate, amount not set";
}

/** One rule's controls. Used for the school default and for a person's override. */
function RuleEditor({ value, onChange, disabled, symbol, isGroup, allowInherit }) {
  const rule = value || {};
  const set = (patch) => onChange({ ...rule, ...patch });

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={12} md={isGroup ? 5 : 4}>
        <FormControl fullWidth sx={inputSx}>
          <InputLabel>They are paid</InputLabel>
          <Select
            value={rule.mode || (allowInherit ? "" : "percent")}
            label="They are paid"
            disabled={disabled}
            onChange={(e) => set({ mode: e.target.value || null })}
          >
            {allowInherit && <MenuItem value="">Follow the school default</MenuItem>}
            {MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>

      {rule.mode === "percent" && (
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth type="number" sx={inputSx} disabled={disabled}
            label="Their share (%)"
            value={rule.percent ?? ""}
            onChange={(e) => set({ percent: e.target.value === "" ? null : parseFloat(e.target.value) })}
            helperText={`The school keeps ${100 - (rule.percent ?? 0)}%`}
          />
        </Grid>
      )}

      {rule.mode === "fixed" && isGroup && (
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth type="number" sx={inputSx} disabled={disabled}
            label={`Per class (${symbol})`}
            value={rule.fixedPerClass ?? ""}
            onChange={(e) => set({ fixedPerClass: e.target.value === "" ? null : parseFloat(e.target.value) })}
            // Per class, not per head: an instructor's income should not depend
            // on how well the front desk filled the room.
            helperText="What they get for running the class, however many attend"
          />
        </Grid>
      )}

      {rule.mode === "fixed" && !isGroup && DURATIONS.map(([key, text]) => (
        <Grid item xs={4} md={2.6} key={key}>
          <TextField
            fullWidth type="number" sx={inputSx} disabled={disabled}
            label={`${text} (${symbol})`}
            value={rule.fixed?.[key] ?? ""}
            onChange={(e) => set({
              fixed: { ...(rule.fixed || {}), [key]: e.target.value === "" ? null : parseFloat(e.target.value) },
            })}
          />
        </Grid>
      ))}

      {rule.mode === "school" && (
        <Grid item xs={12} md={7}>
          <Typography sx={{ ...hint, pt: 1.4 }}>
            Everything the session earns stays with the school. Use this for instructors already on salary.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}

export default function InstructorPay({ tutorsConfig, setTutorsConfig, schoolRegion, feeApplies }) {
  const { enqueueSnackbar } = useSnackbar();
  const symbol = currencySymbol(schoolRegion);

  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    const r = await list_school_instructors_api();
    if (r.code === 200) setInstructors(r.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const comp = tutorsConfig?.compensation || {};

  const saveDefault = async (sessionType, rule) => {
    const next = { ...comp, [sessionType]: rule };
    setTutorsConfig((prev) => ({ ...prev, compensation: next }));
    setSavingDefault(true);
    const r = await update_school_tutors_config_api({ compensation: next });
    setSavingDefault(false);
    // Only failures speak. A save that worked is visible in the field it saved.
    if (r.code !== 200) enqueueSnackbar(r.message || "Could not save that", { variant: "error" });
  };

  const savePerson = async (id, sessionType, rule) => {
    setSavingId(id);
    const r = await update_instructor_compensation_api(id, { [sessionType]: rule });
    setSavingId(null);
    if (r.code === 200) {
      setDrafts((d) => ({ ...d, [id]: undefined }));
      load();
    } else {
      enqueueSnackbar(r.message || "Could not save that", { variant: "error" });
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ ...label, mb: 0.5 }}>Instructor pay</Typography>
      <Typography sx={{ ...hint, mb: 2 }}>
        What your instructors are paid. Separate from what students are charged above —
        {feeApplies ? " this splits what's left after XAir's fee." : " this splits what the session earns."}
      </Typography>

      <Box sx={{ p: 2, border: "1px solid #E8E4DF", borderRadius: "10px", backgroundColor: "#fff", mb: 2 }}>
        <Typography sx={{ ...label, mb: 0.25 }}>1:1 sessions</Typography>
        <Typography sx={{ ...hint, mb: 1.75 }}>The default for everyone who isn't on their own arrangement below.</Typography>
        <RuleEditor
          value={comp.oneToOne} symbol={symbol} disabled={savingDefault}
          onChange={(rule) => saveDefault("oneToOne", rule)}
        />
      </Box>

      <Box sx={{ p: 2, border: "1px solid #E8E4DF", borderRadius: "10px", backgroundColor: "#fff", mb: 2 }}>
        <Typography sx={{ ...label, mb: 0.25 }}>Group classes</Typography>
        <Typography sx={{ ...hint, mb: 1.75 }}>
          Set separately, because one instructor teaching eight students for an hour is different work
          at different economics from an hour with one.
        </Typography>
        <RuleEditor
          value={comp.group} symbol={symbol} disabled={savingDefault} isGroup
          onChange={(rule) => saveDefault("group", rule)}
        />
      </Box>

      <Typography sx={{ ...label, mb: 0.25 }}>People on their own arrangement</Typography>
      <Typography sx={{ ...hint, mb: 1.5 }}>
        Anyone left on "Follow the school default" moves whenever you change the defaults above.
      </Typography>

      {loading ? (
        <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={20} sx={{ color: "#c01e2e" }} /></Box>
      ) : !instructors.length ? (
        <Typography sx={hint}>No instructors yet. This fills once someone is added to your staff.</Typography>
      ) : (
        <Box sx={{ border: "1px solid #E8E4DF", borderRadius: "10px", overflow: "hidden", backgroundColor: "#fff" }}>
          {instructors.map((i) => {
            const name = [i.first_name, i.last_name].filter(Boolean).join(" ") || i.email;
            const resolved = i.compensation || {};
            const draft = drafts[i._id];
            const open = draft !== undefined;
            // "Own arrangement" is what `source` is for — the resolved rule
            // looks identical either way, and only its provenance says whether
            // changing the school default would move this person.
            const ownOneToOne = resolved.oneToOne?.source === "instructor";
            const ownGroup = resolved.group?.source === "instructor";

            return (
              <Box key={i._id} sx={{ px: 1.75, py: 1.4, borderBottom: "1px solid #E8E4DF", "&:last-of-type": { borderBottom: "none" } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                    <Typography sx={{ ...label, fontSize: "13px" }}>{name}</Typography>
                    <Typography sx={{ ...hint, fontSize: "12px" }}>
                      1:1 — {describeRule(resolved.oneToOne, symbol)}{ownOneToOne ? "" : " (school default)"}
                      {"  ·  "}
                      Classes — {describeRule(resolved.group, symbol)}{ownGroup ? "" : " (school default)"}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setDrafts((d) => ({
                      ...d,
                      [i._id]: open ? undefined : {
                        oneToOne: ownOneToOne ? { ...resolved.oneToOne } : { mode: null },
                        group: ownGroup ? { ...resolved.group } : { mode: null },
                      },
                    }))}
                    sx={{
                      textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "12.5px",
                      fontWeight: 600, color: "#1a1a1a", border: "1px solid #E8E4DF", borderRadius: "8px", px: 1.5,
                    }}
                  >
                    {open ? "Close" : "Change"}
                  </Button>
                </Box>

                {open && (
                  <Box sx={{ mt: 1.75, pt: 1.5, borderTop: "1px dashed #E8E4DF" }}>
                    <Typography sx={{ ...hint, fontWeight: 600, color: "#5a5550", mb: 1 }}>1:1 sessions</Typography>
                    <RuleEditor
                      value={draft.oneToOne} symbol={symbol} allowInherit disabled={savingId === i._id}
                      onChange={(rule) => setDrafts((d) => ({ ...d, [i._id]: { ...d[i._id], oneToOne: rule } }))}
                    />
                    <Typography sx={{ ...hint, fontWeight: 600, color: "#5a5550", mt: 2, mb: 1 }}>Group classes</Typography>
                    <RuleEditor
                      value={draft.group} symbol={symbol} allowInherit isGroup disabled={savingId === i._id}
                      onChange={(rule) => setDrafts((d) => ({ ...d, [i._id]: { ...d[i._id], group: rule } }))}
                    />
                    <Button
                      onClick={async () => {
                        await savePerson(i._id, "oneToOne", draft.oneToOne || { mode: null });
                        await savePerson(i._id, "group", draft.group || { mode: null });
                      }}
                      disabled={savingId === i._id}
                      sx={{
                        mt: 2, textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "12.5px",
                        fontWeight: 700, color: "#fff", backgroundColor: "#c01e2e", borderRadius: "8px", px: 2,
                        "&:hover": { backgroundColor: "#a51829" },
                      }}
                    >
                      {savingId === i._id ? "Saving…" : `Save ${name.split(" ")[0]}'s pay`}
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
